import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ExternalLink, Heart, Clock } from "lucide-react";
import { api } from "../lib/api";
import { sanitize, classifyContent } from "../lib/sanitizeHtml";
import { track } from "../lib/analytics";
import { useLocale } from "../context/LocaleContext";
import { useAuth } from "../context/AuthContext";
import { ContentCard } from "../components/ContentCard";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { CalculatorPage } from "./calculators/Calculators";
import NotFound from "./NotFound";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { toast } from "sonner";

const VIEW_EVENT = { article: "article_view", recipe: "recipe_view", exercise: "exercise_view", product: "product_view" };

// Friendly transient-error state (distinct from a real 404) with a retry.
export function LoadError({ onRetry, t }) {
  return (
    <div className="container-page flex min-h-[50vh] flex-col items-center justify-center text-center" data-testid="load-error">
      <h1 className="font-heading text-2xl font-extrabold text-slate-900">{t("loadErrorTitle")}</h1>
      <p className="mt-2 max-w-md text-sm text-slate-500">{t("loadErrorBody")}</p>
      <Button onClick={onRetry} data-testid="load-error-retry" className="mt-6 rounded-full bg-emerald-600 hover:bg-emerald-700">
        {t("retry")}
      </Button>
    </div>
  );
}

function fmtDate(iso, locale) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(locale || undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return ""; }
}

// Localized labels for canonical (language-independent) controlled values.
const TAXO = {
  difficulty: { beginner: { en: "Beginner", es: "Principiante", nl: "Beginner" }, intermediate: { en: "Intermediate", es: "Intermedio", nl: "Gevorderd" }, advanced: { en: "Advanced", es: "Avanzado", nl: "Expert" } },
  movement_type: { isolation: { en: "Isolation", es: "Aislamiento", nl: "Isolatie" }, compound: { en: "Compound", es: "Compuesto", nl: "Samengesteld" }, isometric: { en: "Isometric", es: "Isométrico", nl: "Isometrisch" } },
  muscle: { biceps: { es: "bíceps", nl: "biceps" }, forearms: { es: "antebrazos", nl: "onderarmen" }, quadriceps: { es: "cuádriceps", nl: "quadriceps" }, glutes: { es: "glúteos", nl: "bilspieren" }, hamstrings: { es: "isquiotibiales", nl: "hamstrings" }, core: { es: "core", nl: "core" }, shoulders: { es: "hombros", nl: "schouders" } },
  equipment: { dumbbells: { es: "mancuernas", nl: "dumbbells" }, bodyweight: { es: "peso corporal", nl: "lichaamsgewicht" }, barbell: { es: "barra", nl: "barbell" }, kettlebell: { es: "kettlebell", nl: "kettlebell" }, machines: { es: "máquinas", nl: "machines" } },
  meal_type: { breakfast: { es: "desayuno", nl: "ontbijt" }, lunch: { es: "almuerzo", nl: "lunch" }, dinner: { es: "cena", nl: "diner" }, snack: { es: "tentempié", nl: "snack" }, drink: { es: "bebida", nl: "drankje" }, pre_workout: { en: "pre-workout", es: "pre-entreno", nl: "pre-workout" }, post_workout: { en: "post-workout", es: "post-entreno", nl: "post-workout" } },
};
function tx(kind, value, locale) {
  if (!value) return "";
  const m = TAXO[kind]?.[value];
  return (m && (m[locale] || m.en)) || value;
}
function txList(kind, arr, locale) {
  return (arr || []).map((v) => tx(kind, v, locale)).join(", ");
}

function Chip({ children }) {
  return <span className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-600">{children}</span>;
}

function MetaTags({ items }) {
  const clean = items.filter((x) => x && x.value);
  if (!clean.length) return null;
  return (
    <div className="mt-5 flex flex-wrap gap-2" data-testid="detail-meta-tags">
      {clean.map((x, i) => <Chip key={i}><span className="font-semibold text-slate-800">{x.label}:</span> {x.value}</Chip>)}
    </div>
  );
}

function KeyTakeaways({ items, title }) {
  if (!items?.length) return null;
  return (
    <div className="mt-8 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-6" data-testid="key-takeaways">
      <h2 className="font-heading text-lg font-bold text-emerald-900">{title}</h2>
      <ul className="mt-3 space-y-2">
        {items.map((x, i) => (
          <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-slate-700"><span className="mt-1 text-emerald-600">✓</span>{x}</li>
        ))}
      </ul>
    </div>
  );
}

function BodySections({ sections }) {
  if (!sections?.length) return null;
  return (
    <div className="mt-8 space-y-8" data-testid="body-sections">
      {sections.map((s, i) => (
        <section key={i}>
          <h2 className="font-heading text-xl font-bold text-slate-900">{s.heading}</h2>
          {classifyContent(s.content) === "HTML" ? (
            <div
              className="prose prose-slate mt-2 max-w-none leading-relaxed text-slate-600 overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: sanitize(s.content) }}
            />
          ) : (
            <p className="mt-2 leading-relaxed text-slate-600">{s.content}</p>
          )}
        </section>
      ))}
    </div>
  );
}

function StepList({ title, items, ordered = true, testid, tone = "default" }) {
  if (!items?.length) return null;
  const toneCls = tone === "warn" ? "text-amber-600" : tone === "danger" ? "text-red-600" : "text-emerald-600";
  const Tag = ordered ? "ol" : "ul";
  return (
    <div className="mt-8" data-testid={testid}>
      <h2 className="font-heading text-xl font-bold text-slate-900">{title}</h2>
      <Tag className="mt-3 space-y-2.5">
        {items.map((x, i) => (
          <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-slate-700">
            <span className={`font-bold ${toneCls}`}>{ordered ? `${i + 1}.` : "•"}</span><span>{x}</span>
          </li>
        ))}
      </Tag>
    </div>
  );
}

function References({ items, title }) {
  if (!items?.length) return null;
  return (
    <div className="mt-10 border-t border-slate-200 pt-6" data-testid="references">
      <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
      <ol className="mt-3 space-y-1.5 text-sm text-slate-500">
        {items.map((r, i) => (
          <li key={i}>
            {r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">{r.title || r.url}</a> : (r.title || "")}
            {r.publisher ? ` — ${r.publisher}` : ""}{r.published_at ? ` (${r.published_at})` : ""}
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function DetailPage({ preview = false }) {
  const { section, slug, id } = useParams();
  const { locale, t, lp, sp, setPageAlternates, resolveSection, config } = useLocale();
  const canonical = resolveSection(section);
  const { user, isStaff, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!config) return;
    if (preview) {
      // DEV staff draft preview: wait for auth, deny non-staff, fetch via the
      // staff-gated read-only endpoint, and render through this same component.
      if (authLoading) return;
      if (!isStaff) { setNotFound(true); return; }
      setData(null); setNotFound(false); setError(false);
      api.get(`/api/admin/preview/${id}?locale=${locale}`)
        .then(({ data }) => {
          setData(data);
          if (data.url_alternates) setPageAlternates(data.url_alternates);
        })
        .catch((err) => {
          if (err?.response?.status === 404) setNotFound(true);
          else setError(true);
        });
      return;
    }
    if (canonical === "calculators") return;
    setData(null); setNotFound(false); setError(false);
    api.get(`/api/public/detail/${section}/${slug}?locale=${locale}`)
      .then(({ data }) => {
        setData(data);
        track(VIEW_EVENT[data.type] || "page_view", { locale, content_id: data.id });
        if (data.url_alternates) setPageAlternates(data.url_alternates);
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else setError(true);
      });
  }, [preview, id, section, slug, locale, canonical, config, isStaff, authLoading, setPageAlternates, reload]);

  const toggleFavorite = useCallback(async () => {
    if (!user) { toast.error(t("login")); return; }
    try {
      if (saved) { await api.delete(`/api/me/favorites/${data.id}`); setSaved(false); }
      else { await api.post("/api/me/favorites", { content_id: data.id }); setSaved(true); track("favorite_added", { content_id: data.id }); toast.success(t("saved")); }
    } catch (e) { /* ignore */ }
  }, [user, saved, data, t]);

  if (canonical === "calculators") return <CalculatorPage />;

  if (notFound && config) return <NotFound />;
  if (error && config) return <LoadError onRetry={() => setReload((n) => n + 1)} t={t} />;
  if (!data) return <div className="container-page py-24 text-center text-slate-400">{t("loading")}</div>;

  const c = data.content;
  const d = data.data || {};
  const sectionSlug = section || data.section;
  const related = data.related || {};
  const relatedFlat = Object.entries(related).flatMap(([, arr]) => arr).filter(Boolean);
  const relatedUnique = Array.from(new Map(relatedFlat.map((x) => [x.id, x])).values()).slice(0, 6);

  return (
    <>
      <Seo seo={data.seo} />
      {preview && (
        <div data-testid="draft-preview-banner" className="sticky top-0 z-50 bg-amber-500 px-4 py-2 text-center text-sm font-semibold text-white">
          {t("draftPreviewBanner") !== "draftPreviewBanner" ? t("draftPreviewBanner") : "DRAFT PREVIEW"} · {String(data.locale || locale).toUpperCase()}
        </div>
      )}
      <article className="container-page py-10 lg:py-14">
        <Breadcrumbs items={[
          { name: "LIVYVIO", to: lp("") },
          { name: data.seo?.breadcrumbs?.[1]?.name || sectionSlug, to: lp(`/${sectionSlug}`) },
          { name: c.title },
        ]} />

        <div className="mt-6 grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-3xl font-black leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">{c.title}</h1>
            {c.excerpt && <p className="mt-4 text-lg leading-relaxed text-slate-600">{c.excerpt}</p>}

            {/* Editorial trust byline */}
            {(data.author || data.updated_at || d.estimated_read_minutes) && (
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500" data-testid="detail-byline">
                {data.author?.name && <span data-testid="detail-author">{t("byAuthor")} {data.author.name}</span>}
                {d.estimated_read_minutes && <span>· {d.estimated_read_minutes} {t("minRead")}</span>}
                {data.updated_at && <span>· {t("updatedOn")} {fmtDate(data.updated_at, locale)}</span>}
                {data.reviewer?.name && <span data-testid="detail-reviewer">· {t("reviewedOn")} {data.last_reviewed_at ? fmtDate(data.last_reviewed_at, locale) : ""} · {data.reviewer.name}</span>}
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {d.difficulty && <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">{tx("difficulty", d.difficulty, locale)}</span>}
              <Button onClick={toggleFavorite} variant="outline" size="sm" data-testid="favorite-btn"
                className={`ml-auto rounded-full ${saved ? "border-emerald-300 bg-emerald-50 text-emerald-700" : ""}`}>
                <Heart className={`mr-1.5 h-4 w-4 ${saved ? "fill-emerald-600 text-emerald-600" : ""}`} />
                {saved ? t("saved") : t("saveFavorite")}
              </Button>
            </div>

            {data.featured_image_url && (
              <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200">
                <img src={data.featured_image_url} alt={c.title} loading="lazy" className="max-h-[70vh] w-full object-cover" />
              </div>
            )}

            {/* Article: key takeaways before body */}
            {data.type === "article" && <KeyTakeaways items={d.key_takeaways} title={t("keyTakeaways")} />}

            {/* Intro / body prose */}
            {d.intro &&
              (classifyContent(d.intro) === "HTML" ? (
                <div
                  data-testid="detail-intro-html"
                  className="prose prose-slate mt-8 max-w-none text-lg leading-relaxed text-slate-700"
                  dangerouslySetInnerHTML={{ __html: sanitize(d.intro) }}
                />
              ) : (
                <p data-testid="detail-intro-text" className="mt-8 text-lg leading-relaxed text-slate-700">
                  {d.intro}
                </p>
              ))}
            {c.body && <div className="prose prose-slate mt-6 max-w-none leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitize(c.body) }} />}

            {/* Article structured sections + references */}
            {data.type === "article" && <BodySections sections={d.body_sections} />}

            {/* Recipe */}
            {data.type === "recipe" && (
              <>
                <MetaTags items={[
                  { label: t("prepTime"), value: d.prep_minutes ? `${d.prep_minutes} ${t("minutesShort")}` : "" },
                  { label: t("cookTime"), value: d.cook_minutes ? `${d.cook_minutes} ${t("minutesShort")}` : "" },
                  { label: t("totalTime"), value: d.total_minutes ? `${d.total_minutes} ${t("minutesShort")}` : "" },
                  { label: t("servings"), value: d.servings ? String(d.servings) : "" },
                  { label: t("mealTypes"), value: txList("meal_type", d.meal_types, locale) },
                  { label: t("dietaryTags"), value: (d.dietary_tags || []).join(", ") },
                ]} />
                {d.ingredients?.length > 0 && (
                  <div className="mt-8" data-testid="recipe-ingredients">
                    <h2 className="font-heading text-xl font-bold text-slate-900">{t("ingredients")}</h2>
                    <ul className="mt-3 space-y-2 text-sm text-slate-700">
                      {d.ingredients.map((x, i) => (
                        <li key={i} className="flex gap-2"><span className="text-emerald-500">•</span>
                          {typeof x === "string" ? x : [x.quantity, x.unit, x.ingredient].filter(Boolean).join(" ")}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <StepList title={t("instructions")} items={d.instructions} testid="recipe-instructions" />
                {d.estimated_nutrition && (
                  <div className="mt-8" data-testid="recipe-nutrition">
                    <h2 className="font-heading text-xl font-bold text-slate-900">{t("nutritionEstimate")}</h2>
                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                      {[["calories", t("calories"), ""], ["protein_g", t("proteinLabel"), "g"], ["carbs_g", t("carbsLabel"), "g"], ["fat_g", t("fatLabel"), "g"], ["fiber_g", t("fiberLabel"), "g"]]
                        .filter(([k]) => d.estimated_nutrition[k] != null)
                        .map(([k, label, unit]) => (
                          <div key={k} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
                            <div className="font-heading text-xl font-bold text-emerald-600">{d.estimated_nutrition[k]}{unit}</div>
                            <div className="text-xs text-slate-500">{label}</div>
                          </div>
                        ))}
                    </div>
                    <p className="mt-2 text-xs text-slate-500">{t("nutritionNote")}</p>
                  </div>
                )}
                <StepList title={t("tips")} items={d.tips} ordered={false} testid="recipe-tips" />
              </>
            )}

            {/* Exercise */}
            {data.type === "exercise" && (
              <>
                {d.video_url && (
                  <div className="mt-8 aspect-video overflow-hidden rounded-2xl border border-slate-200">
                    <iframe src={d.video_url} title={c.title} className="h-full w-full" allowFullScreen />
                  </div>
                )}
                <MetaTags items={[
                  { label: t("primaryMuscles"), value: txList("muscle", d.primary_muscles, locale) },
                  { label: t("secondaryMuscles"), value: txList("muscle", d.secondary_muscles, locale) },
                  { label: t("equipment"), value: txList("equipment", d.equipment, locale) },
                  { label: t("movementType"), value: tx("movement_type", d.movement_type, locale) },
                  { label: t("setsReps"), value: d.suggested_sets_reps || "" },
                ]} />
                <StepList title={t("howToTitle")} items={d.instructions} testid="exercise-instructions" />
                <StepList title={t("commonMistakes")} items={d.common_mistakes} ordered={false} testid="exercise-mistakes" tone="warn" />
                <StepList title={t("coachingTips")} items={d.coaching_tips} ordered={false} testid="exercise-tips" />
                {d.safety_note && (
                  <div className="mt-8 rounded-2xl border border-red-100 bg-red-50 p-5" data-testid="exercise-safety">
                    <h2 className="font-heading text-base font-bold text-red-800">{t("safetyNote")}</h2>
                    <p className="mt-1.5 text-sm leading-relaxed text-red-700">{d.safety_note}</p>
                  </div>
                )}
                <StepList title={t("alternatives")} items={d.alternatives} ordered={false} testid="exercise-alternatives" />
              </>
            )}

            {/* References (article/recipe/exercise) */}
            <References items={d.references} title={t("sources")} />

            {/* Product external offers */}
            {data.type === "product" && (
              <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <h3 className="font-heading text-lg font-bold text-slate-900">{t("externalOffers")}</h3>
                <p className="mt-1 text-sm text-slate-500">{t("externalNote")}</p>
                <div className="mt-4 space-y-3">
                  {(data.offers || []).map((o, i) => (
                    <a key={i} href={o.url} target="_blank" rel="noopener noreferrer nofollow sponsored"
                      data-testid={`offer-${i}`}
                      onClick={() => track(o.is_affiliate ? "affiliate_click" : "external_product_click", { content_id: data.id, props: { merchant: o.merchant_slug } })}
                      className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-emerald-300 hover:shadow-sm">
                      <div>
                        <div className="font-semibold text-slate-900">{o.merchant?.name || o.label}</div>
                        <div className="text-xs text-slate-500">
                          {o.price ? `${o.price} ${o.currency}` : ""} {o.is_affiliate ? `· ${t("affiliate")}` : ""}
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                        {t("visitStore")} <ExternalLink className="h-4 w-4" />
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Health disclaimer */}
            {data.health_disclaimer_applicable && (
              <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs leading-relaxed text-slate-500" data-testid="health-disclaimer">
                <strong className="text-slate-600">{t("healthDisclaimer")}:</strong> {t("healthDisclaimerBody")}
              </div>
            )}

            {/* FAQ */}
            {c.faq?.length > 0 && (
              <div className="mt-12">
                <h3 className="font-heading text-xl font-bold text-slate-900">{t("faq")}</h3>
                <Accordion type="single" collapsible className="mt-4">
                  {c.faq.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`}>
                      <AccordionTrigger className="text-left" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
                      <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>

          {/* Related sidebar */}
          <aside className="lg:col-span-1">
            {relatedUnique.length > 0 && (
              <div className="sticky top-24">
                <h3 className="font-heading text-lg font-bold text-slate-900">{t("relatedContent")}</h3>
                <div className="mt-4 grid gap-5">
                  {relatedUnique.map((it, i) => <ContentCard key={it.id} item={it} index={i} />)}
                </div>
              </div>
            )}
          </aside>
        </div>
      </article>
    </>
  );
}
